import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchBar from "./SearchBar";
import CheckMinusSymbols from "./CheckMinusSymbols";
import { getAvailableSoftwareUpdates, getOSUpdateStatus, scheduleOSUpdate } from "../commands/mdmCommands";
import isAdministrator from "../utilities/checkPrivileges";
import softwareUpdateService, {
  defaultDeadlineLocal,
  softwareUpdateFamilyLabel,
  softwareUpdateVersionLabel,
  toTargetLocalDateTime,
} from "../services/softwareUpdateService";
import { toast } from "react-toastify";

function normalize(status = "") {
  return String(status).toLowerCase();
}

function isDownloadedStatus(entry) {
  if (!entry) return false;
  const downloadedFlag = entry.IsDownloaded;
  const isDownloaded =
    downloadedFlag === true ||
    downloadedFlag === 1 ||
    String(downloadedFlag).toLowerCase() === "true";
  if (isDownloaded) return true;

  const pctRaw = entry.DownloadPercentComplete;
  const pct = typeof pctRaw === "number" ? pctRaw : Number(pctRaw);
  const idle = normalize(entry.Status) === "idle";
  return idle && Number.isFinite(pct) && pct >= 1;
}

function sameUpdateKey(left, right) {
  const a = String(left || "").trim();
  const b = String(right || "").trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const shortA = a.replace(/^(tvOS|iOS|macOS)Update/i, "");
  const shortB = b.replace(/^(tvOS|iOS|macOS)Update/i, "");
  if (a.startsWith(b) || b.startsWith(a)) return true;
  return Boolean(shortA && shortB && (shortA === shortB || a.endsWith(shortB) || b.endsWith(shortA)));
}

function pickStatusEntry(productKey, rows = []) {
  const list = Array.isArray(rows) ? rows.filter((row) => row?.ProductKey) : [];
  if (!list.length) return null;
  const exact = list.find((row) => row.ProductKey === productKey);
  if (exact) return exact;
  const loose = list.find((row) => sameUpdateKey(row.ProductKey, productKey));
  if (loose) return loose;
  if (list.length === 1) return list[0];
  return null;
}

const INSTALL_STATE_LABELS = {
  none: "kein Update",
  waiting: "wartet",
  downloading: "lädt",
  prepared: "bereit",
  installing: "installiert",
  failed: "fehlgeschlagen",
};

function statusValue(values, key) {
  if (!values || typeof values !== "object") return null;
  if (values[key] != null) return values[key];
  const nested = values[key.split(".")[0]];
  if (nested && typeof nested === "object") {
    const rest = key.split(".").slice(1).join(".");
    if (nested[rest] != null) return nested[rest];
  }
  return null;
}

function formatStatusObject(value) {
  if (value == null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatStatusObject(item)).join(", ");
  }
  const os = value.OSVersion || value.osVersion || value.ProductVersion || value.productVersion;
  const build = value.BuildVersion || value.buildVersion;
  if (os || build) return [os, build].filter(Boolean).join(" · ");
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export default function SoftwareUpdatesTable({
  updates = [],
  osUpdateStatus = [],
  osUpdateResults = [],
  udid,
  serialNumber,
  osVersion,
  availableDeviceCapacity,
  deviceCapacity,
  dataTimestamp,
  onDataChanged
}) {
  const [query, setQuery] = useState("");
  const [policyState, setPolicyState] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("inherit");
  const [pinVersion, setPinVersion] = useState("");
  const [deadline, setDeadline] = useState(defaultDeadlineLocal());
  const canAct = isAdministrator() && Boolean(udid);
  const ddmActive = Boolean(policyState?.ddmActive);

  const loadPolicy = useCallback(async () => {
    if (!udid) return;
    setPolicyLoading(true);
    try {
      const data = await softwareUpdateService.getDeviceSoftwareUpdate(udid, serialNumber);
      setPolicyState(data);
      const policy = data.policy || {};
      setMode(policy.mode === "none" || policy.mode === "pin" ? policy.mode : "inherit");
      setPinVersion(policy.mode === "pin" ? (policy.productVersion || "") : "");
    } catch {
      setPolicyState(null);
    } finally {
      setPolicyLoading(false);
    }
  }, [udid, serialNumber]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const statusByProductKey = useMemo(() => {
    const map = {};
    (osUpdateStatus || []).forEach((s) => {
      if (!s?.ProductKey) return;
      map[s.ProductKey] = s;
    });
    return map;
  }, [osUpdateStatus]);

  const resultByProductKey = useMemo(() => {
    const map = {};
    (osUpdateResults || []).forEach((r) => {
      if (!r?.ProductKey) return;
      map[r.ProductKey] = r;
    });
    return map;
  }, [osUpdateResults]);

  const liveBusy = useMemo(() => {
    const statusBusy = (osUpdateStatus || []).some((entry) => {
      const status = normalize(entry?.Status);
      return status === "downloading" || status === "installing";
    });
    const resultBusy = (osUpdateResults || []).some((entry) => normalize(entry?.Status) === "installing");
    return statusBusy || resultBusy;
  }, [osUpdateStatus, osUpdateResults]);

  useEffect(() => {
    if (!udid || !liveBusy) return undefined;
    const tick = () => {
      getOSUpdateStatus(udid, { silent: true }).then(() => {
        if (typeof onDataChanged === "function") onDataChanged();
      }).catch(() => {});
    };
    const id = window.setInterval(tick, 60 * 1000);
    return () => window.clearInterval(id);
  }, [udid, liveBusy, onDataChanged]);

  const filteredAndSortedUpdates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const arr = [...(updates || [])];
    arr.sort((a, b) => (a.HumanReadableName || "").localeCompare(b.HumanReadableName || ""));
    if (!q) return arr;
    return arr.filter((u) =>
      (u.HumanReadableName || "").toLowerCase().includes(q) ||
      (u.Version || "").toLowerCase().includes(q) ||
      (u.ProductKey || "").toLowerCase().includes(q)
    );
  }, [updates, query]);

  const isBusy = (productKey) => {
    const status = normalize(pickStatusEntry(productKey, osUpdateStatus)?.Status);
    return status === "downloading" || status === "installing";
  };

  const getErrorLabel = (productKey) => {
    const result = resultByProductKey[productKey];
    const chain = Array.isArray(result?.ErrorChain) ? result.ErrorChain : [];
    if (!chain.length) return "";
    const first = chain[0];
    const msg = first?.LocalizedDescription || first?.USEnglishDescription || "Update-Fehler";
    const code = first?.ErrorCode !== undefined ? ` (${first.ErrorCode})` : "";
    return `${msg}${code}`;
  };

  const getProgressLabel = (productKey) => {
    const s = pickStatusEntry(productKey, osUpdateStatus) || statusByProductKey[productKey];
    if (!s) return "—";
    if (typeof s.DownloadPercentComplete === "number") {
      return `${Math.round(s.DownloadPercentComplete * 100)}%`;
    }
    return s.Status || "—";
  };

  const toBytes = (value) => {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num) || num < 0) return null;
    if (num > 10_000) return num;
    return num * 1024 * 1024 * 1024;
  };

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 0) return "—";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let val = bytes;
    let idx = 0;
    while (val >= 1024 && idx < units.length - 1) {
      val /= 1024;
      idx += 1;
    }
    return `${val.toFixed(idx >= 3 ? 2 : 0)} ${units[idx]}`;
  };

  const formatTimestamp = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-DE");
  };

  const runAndRefresh = async (runner) => {
    try {
      await runner();
    } finally {
      if (typeof onDataChanged === "function") onDataChanged();
    }
  };

  const runClassicCommand = async (runner) => {
    await runAndRefresh(async () => {
      await runner();
      await getOSUpdateStatus(udid, { silent: true });
    });
  };

  const savePolicy = async () => {
    if (!udid) return;
    if (mode === "pin" && !pinVersion) {
      toast.error("Bitte eine Zielversion wählen.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        mode,
        serialNumber,
        family: policyState?.family || "ios",
      };
      if (mode === "pin") {
        payload.productVersion = pinVersion;
        payload.targetLocalDateTime = toTargetLocalDateTime(deadline);
      }
      const result = await softwareUpdateService.setDeviceSoftwareUpdate(udid, payload);
      toast.success("OS-Update-Vorgabe gespeichert.");
      await loadPolicy();
      if (result?.policy) {
        setMode(result.policy.mode === "none" || result.policy.mode === "pin" ? result.policy.mode : "inherit");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const resetToGroup = async () => {
    if (!udid) return;
    setSaving(true);
    try {
      await softwareUpdateService.setDeviceSoftwareUpdate(udid, { mode: "inherit", serialNumber });
      toast.success("Geräte-Override zurückgesetzt. Es gilt wieder die Gruppe.");
      await loadPolicy();
      setMode("inherit");
      setPinVersion("");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Zurücksetzen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const policy = policyState?.policy || {};
  const values = policyState?.statusValues || {};
  const installState = statusValue(values, "softwareupdate.install-state");
  const pendingVersion = statusValue(values, "softwareupdate.pending-version");
  const failureReason = statusValue(values, "softwareupdate.failure-reason");
  const installReason = statusValue(values, "softwareupdate.install-reason");
  const catalog = policyState?.catalog || [];
  const showClassicActions = canAct && !ddmActive;
  const actionCols = showClassicActions ? 9 : 8;

  return (
    <div>
      <div className="card mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
            <h6 className="mb-0">DDM OS-Update</h6>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={policyLoading || !udid}
              onClick={loadPolicy}
            >
              Status aktualisieren
            </button>
          </div>
          {policyLoading && !policyState ? (
            <p className="text-muted mb-0">Lade Vorgabe und Status…</p>
          ) : (
            <>
              <dl className="row mb-3 small">
                <dt className="col-sm-3">Installierte Version</dt>
                <dd className="col-sm-9">{osVersion || policyState?.osVersion || "—"}</dd>
                <dt className="col-sm-3">Zielversion</dt>
                <dd className="col-sm-9">
                  {policy.effectiveIdentifier
                    ? softwareUpdateVersionLabel(policy.effectiveIdentifier)
                    : "keine Enforcement"}
                  {policy.source === "group" && policy.groupName ? (
                    <span className="text-muted"> · Gruppe {policy.groupName}</span>
                  ) : null}
                  {policy.overridden ? (
                    <span className="badge text-bg-warning ms-2">Geräte-Override</span>
                  ) : null}
                </dd>
                <dt className="col-sm-3">DDM-Status</dt>
                <dd className="col-sm-9">
                  {installState
                    ? (INSTALL_STATE_LABELS[String(installState)] || String(installState))
                    : (ddmActive ? "zugewiesen, noch kein Report" : "nicht aktiv")}
                  {pendingVersion ? (
                    <span className="text-muted"> · anstehend {formatStatusObject(pendingVersion)}</span>
                  ) : null}
                </dd>
                {installReason ? (
                  <>
                    <dt className="col-sm-3">Auslöser</dt>
                    <dd className="col-sm-9">{formatStatusObject(installReason)}</dd>
                  </>
                ) : null}
                {failureReason ? (
                  <>
                    <dt className="col-sm-3">Fehler</dt>
                    <dd className="col-sm-9 text-danger">{formatStatusObject(failureReason)}</dd>
                  </>
                ) : null}
              </dl>

              {ddmActive && (
                <p className="small text-muted">
                  Solange eine Software-Update-DDM aktiv ist, steuert Apple das Update über Declarative Device Management.
                  Download / Install ASAP bleiben ausgeblendet, Statusabfrage über MDM ist weiterhin möglich.
                </p>
              )}

              {canAct && (
                <div className="border-top pt-3">
                  <p className="small text-muted">
                    Die Gruppe setzt die Standardversion. Am Gerät kann sie überschrieben oder eine Zwischenversion festgepinnt werden.
                    Es gilt immer nur eine Software-Update-DDM.
                  </p>
                  <div className="d-flex flex-column gap-1 mb-2">
                    <label className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="su-mode"
                        checked={mode === "inherit"}
                        onChange={() => setMode("inherit")}
                      />
                      <span className="form-check-label">
                        Gruppenvorgabe
                        {policy.groupIdentifier
                          ? ` (${softwareUpdateVersionLabel(policy.groupIdentifier)})`
                          : " (keine)"}
                      </span>
                    </label>
                    <label className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="su-mode"
                        checked={mode === "none"}
                        onChange={() => setMode("none")}
                      />
                      <span className="form-check-label">Kein OS-Update über DDM</span>
                    </label>
                    <label className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="su-mode"
                        checked={mode === "pin"}
                        onChange={() => setMode("pin")}
                      />
                      <span className="form-check-label">
                        Version festpinnen ({softwareUpdateFamilyLabel(policyState?.family)})
                      </span>
                    </label>
                  </div>
                  {mode === "pin" && (
                    <div className="row g-2 mb-2">
                      <div className="col-md-6">
                        <label className="form-label small">Zielversion aus GDMF</label>
                        <select
                          className="form-select form-select-sm"
                          value={pinVersion}
                          onChange={(e) => setPinVersion(e.target.value)}
                        >
                          <option value="">Version wählen…</option>
                          {catalog.map((item) => (
                            <option key={item.identifier} value={item.productVersion}>
                              {item.productVersion}
                              {item.build ? ` (${item.build})` : ""}
                              {item.postingDate ? ` · ${item.postingDate}` : ""}
                              {item.installed ? " · aktuell" : item.upgrade ? " · Update" : ""}
                            </option>
                          ))}
                        </select>
                        <div className="small text-muted mt-1">
                          DDM kann nicht downgraden. Es erscheinen nur Versionen ab {osVersion || policyState?.osVersion || "der installierten Version"}, die zu diesem Modell passen.
                        </div>
                        {policyState?.catalogError ? (
                          <div className="small text-danger mt-1">{policyState.catalogError}</div>
                        ) : null}
                        {!policyState?.catalogError && catalog.length === 0 ? (
                          <div className="small text-muted mt-1">Keine passenden GDMF-Versionen für dieses Gerät.</div>
                        ) : null}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">Deadline (lokal)</label>
                        <input
                          type="datetime-local"
                          className="form-control form-control-sm"
                          value={deadline}
                          onChange={(e) => setDeadline(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={saving}
                      onClick={savePolicy}
                    >
                      {saving ? "Speichere…" : "Vorgabe speichern"}
                    </button>
                    {policy.overridden && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={saving}
                        onClick={resetToGroup}
                      >
                        Override zurücksetzen
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="d-flex gap-2 mb-2">
        <div style={{ minWidth: "220px", maxWidth: "320px" }}>
          <SearchBar searchHandler={setQuery} value={query} />
        </div>
        {canAct && (
          <>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => runAndRefresh(() => getAvailableSoftwareUpdates(udid))}
            >
              Refresh Updates
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => runAndRefresh(() => getOSUpdateStatus(udid))}
            >
              Refresh Status
            </button>
          </>
        )}
      </div>

      <table className='table tableList'>
        <thead>
          <tr>
            <th>Name</th>
            <th>Version</th>
            <th>Speicherbedarf</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Critical</th>
            <th>Security</th>
            <th>Restart</th>
            {showClassicActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedUpdates.map((update, index) => {
            const productKey = update.ProductKey;
            const statusEntry = pickStatusEntry(productKey, osUpdateStatus);
            const resultEntry = pickStatusEntry(productKey, osUpdateResults) || resultByProductKey[productKey];
            const busy = isBusy(productKey);
            const downloaded = isDownloadedStatus(statusEntry);
            const availableBytes = toBytes(availableDeviceCapacity);
            const totalBytes = toBytes(deviceCapacity);
            const requiredBytes = Number(update.InstallSize) || Number(update.DownloadSize) || 0;
            const insufficientStorage =
              Number.isFinite(availableBytes) &&
              Number.isFinite(requiredBytes) &&
              requiredBytes > 0 &&
              requiredBytes > availableBytes;
            const dataTs = formatTimestamp(dataTimestamp);

            return (
              <tr key={`${productKey || "no-key"}-${index}`}>
                <td>{update.HumanReadableName || update.ProductName || "Unknown update"}</td>
                <td>{update.Version || "—"}</td>
                <td className={insufficientStorage ? "text-danger fw-semibold" : ""}>
                  {formatBytes(requiredBytes)}
                  {Number(update.DownloadSize) > 0 && Number(update.InstallSize) > 0 && (
                    <div className="small text-muted">
                      DL: {formatBytes(Number(update.DownloadSize))}
                    </div>
                  )}
                  {Number.isFinite(availableBytes) && (
                    <div className="small text-muted">
                      frei: {formatBytes(availableBytes)}
                      {Number.isFinite(totalBytes) ? ` / ${formatBytes(totalBytes)}` : ""}
                    </div>
                  )}
                  {insufficientStorage && (
                    <div className="small">zu wenig freier Speicher</div>
                  )}
                </td>
                <td>{statusEntry?.Status || resultEntry?.Status || "idle"}</td>
                <td>
                  {getProgressLabel(productKey)}
                  {dataTs && (
                    <div className="small text-muted">{dataTs}</div>
                  )}
                </td>
                <td><CheckMinusSymbols status={update.IsCritical} /></td>
                <td><CheckMinusSymbols status={update.IsSecurityResponse} /></td>
                <td><CheckMinusSymbols status={update.RestartRequired} /></td>
                {showClassicActions && (
                  <td className="d-flex gap-1">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!productKey || busy}
                      title={busy ? "Update ist aktuell beschäftigt" : "Nur Download starten"}
                      onClick={() => runClassicCommand(() => scheduleOSUpdate(udid, productKey, "DownloadOnly"))}
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!productKey || busy || !downloaded}
                      title={!downloaded ? "Install erst nach abgeschlossenem Download" : "Update sofort installieren"}
                      onClick={() => runClassicCommand(() => scheduleOSUpdate(udid, productKey, "InstallASAP"))}
                    >
                      Install ASAP
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
          {filteredAndSortedUpdates.length === 0 && (
            <tr>
              <td colSpan={actionCols} className="text-muted">
                {ddmActive
                  ? "Keine klassischen MDM-Update-Einträge. Fortschritt steht oben im DDM-Status."
                  : "Keine verfügbaren Updates gemeldet."}
              </td>
            </tr>
          )}
          {filteredAndSortedUpdates.map((update, index) => {
            const productKey = update.ProductKey;
            const err = getErrorLabel(productKey);
            if (!err) return null;
            return (
              <tr key={`err-${productKey || index}`}>
                <td colSpan={actionCols} className="text-danger small">
                  Letzter Install-Fehler: {err}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
